import React, { useEffect, useRef } from 'react';
import { MarkdownContent } from './MarkdownContent';
// Once scrolled back within this many pixels of the bottom, re-arm
// auto-follow.
const NEAR_BOTTOM_THRESHOLD_PX = 80;
// Ignore scrollTop jitter this small (subpixel rounding, momentum/rubber-band
// noise right at the bottom) so it isn't mistaken for a deliberate scroll up.
const SCROLL_UP_EPSILON_PX = 4;
export const MessageList = ({ messages, isReplying, showTypingIndicator }) => {
    const containerRef = useRef(null);
    const bottomRef = useRef(null);
    // Starts true so the first message (or the panel's initial state) still
    // scrolls into view; updated on every scroll, including our own
    // programmatic ones below, so it stays accurate either way.
    const isNearBottomRef = useRef(true);
    const previousMessageCountRef = useRef(messages.length);
    const lastScrollTopRef = useRef(0);
    const handleScroll = () => {
        const container = containerRef.current;
        if (!container)
            return;
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const scrolledUp = container.scrollTop < lastScrollTopRef.current;
        lastScrollTopRef.current = container.scrollTop;
        // Any deliberate upward movement counts as "scrolled away" immediately,
        // with no distance threshold to clear — a slow wheel/trackpad gesture
        // moves in small increments, and a streamed reply's frequent re-checks
        // would otherwise snap back to the bottom before the user could ever
        // cross a larger threshold, effectively fighting their scroll. Reaching
        // back near the bottom (either direction) still re-arms auto-follow.
        if (scrolledUp && distanceFromBottom > SCROLL_UP_EPSILON_PX) {
            isNearBottomRef.current = false;
            return;
        }
        isNearBottomRef.current = distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX;
    };
    // Keep the latest message (or the typing indicator) in view. A new message
    // (the user's own send, or the placeholder that follows it) always scrolls
    // — sending is an explicit "I'm back at the bottom" signal, so it must
    // still work after the user scrolled up to reread earlier context. Only a
    // streamed reply growing an *existing* message's `content` in place (same
    // length, new array reference via `.map()`) is guarded by
    // `isNearBottomRef`, since that shouldn't fight a deliberate manual scroll.
    // `'auto'` (instant) rather than `'smooth'`, since restarting a
    // smooth-scroll animation on every token looks janky rather than like one
    // continuous scroll.
    useEffect(() => {
        const messageCountChanged = messages.length !== previousMessageCountRef.current;
        previousMessageCountRef.current = messages.length;
        if (messageCountChanged || isNearBottomRef.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'auto' });
        }
    }, [messages, isReplying]);
    return (React.createElement("div", { "aria-live": "polite", className: "ai-assistant__messages", "data-ph-mask": true, onScroll: handleScroll, ref: containerRef, role: "log" },
        messages.length === 0 && !isReplying && (React.createElement("p", { className: "ai-assistant__empty" }, "Ask a question to get started.")),
        messages
            .filter((_message, index) => !(showTypingIndicator && index === messages.length - 1))
            .map((message) => (React.createElement("div", { className: `ai-assistant__message ai-assistant__message--${message.role}`, key: message.id }, message.role === 'assistant' ? (React.createElement(MarkdownContent, { content: message.content })) : (message.content)))),
        showTypingIndicator && (React.createElement("div", { "aria-label": "Assistant is typing", className: "ai-assistant__message ai-assistant__message--assistant ai-assistant__typing" },
            React.createElement("span", { className: "ai-assistant__typing-dot" }),
            React.createElement("span", { className: "ai-assistant__typing-dot" }),
            React.createElement("span", { className: "ai-assistant__typing-dot" }))),
        React.createElement("div", { ref: bottomRef })));
};
